import React, { useState } from 'react';
import { Loader2, AlertCircle, Zap, LogIn, Info } from 'lucide-react';
import { usePlans } from '../../hooks/useSubscription';
import { PlanCard } from './PlanCard';

export function PricingSection({
  userId,
  currentPlanId,
  onSubscribeSuccess,
  onSubscribeError,
  onLoginRequired,
  className = '',
}) {
  const { plans, loading, error } = usePlans();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleSelectPlan = async (planId) => {
    if (!userId) {
      setShowLoginPrompt(true);
      onLoginRequired?.();
      return;
    }
    // Razorpay not integrated yet — show coming soon
    setShowComingSoon(true);
    setTimeout(() => setShowComingSoon(false), 5000);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-20 ${className}`}>
        <Loader2 className="animate-spin text-cyan-500" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center py-20 ${className}`}>
        <AlertCircle className="text-red-500 mb-4" size={48} />
        <p className="text-red-400 font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Sort plans: monthly first, then by price
  const sortedPlans = [...plans].sort((a, b) => {
    if (a.billing_cycle === 'monthly' && b.billing_cycle === 'annual') return -1;
    if (a.billing_cycle === 'annual' && b.billing_cycle === 'monthly') return 1;
    return a.price - b.price;
  });

  return (
    <section className={`py-20 ${className}`} data-testid="pricing-section">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-cyan-500/20">
          <Zap size={16} />
          Simple, Transparent Pricing
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Choose Your Plan
        </h2>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto">
          Start managing your family's health with Nextdose. 
          All plans include our core features with no hidden fees.
        </p>
      </div>

      {/* Coming Soon Message */}
      {showComingSoon && (
        <div className="max-w-md mx-auto mb-8 flex items-center gap-3 text-cyan-400 bg-cyan-500/10 p-4 rounded-lg border border-cyan-500/20" data-testid="coming-soon-msg">
          <Info size={20} className="flex-shrink-0" />
          <span>Payment integration coming soon! We'll notify you when subscriptions are live.</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-md mx-auto mb-8 flex items-center gap-2 text-red-400 bg-red-500/10 p-4 rounded-lg border border-red-500/20">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Login Prompt */}
      {showLoginPrompt && !userId && (
        <div className="max-w-md mx-auto mb-8 flex items-center justify-between gap-4 text-cyan-400 bg-cyan-500/10 p-4 rounded-lg border border-cyan-500/20">
          <div className="flex items-center gap-2">
            <LogIn size={20} />
            <span>Please log in or sign up to subscribe</span>
          </div>
          <div className="flex gap-2">
            <a 
              href="/auth?mode=login&redirect=/pricing" 
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm font-medium whitespace-nowrap"
            >
              Login
            </a>
            <a 
              href="/auth?mode=signup&redirect=/pricing" 
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors text-sm font-medium whitespace-nowrap"
            >
              Sign Up
            </a>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
        {sortedPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            onSelect={handleSelectPlan}
            isLoading={false}
            currentPlanId={currentPlanId}
          />
        ))}
      </div>

      {/* Footer Note */}
      <div className="text-center mt-12 text-slate-500">
        <p className="text-sm">
          All prices are in INR. Subscriptions auto-renew unless cancelled.
        </p>
        <p className="text-sm mt-1">
          Have questions? <a href="/contact" className="text-cyan-400 hover:underline">Contact our support team</a>
        </p>
      </div>
    </section>
  );
}

export default PricingSection;
